import { useEffect, useState } from "react";
import { Play, X } from "lucide-react";
import { videosApi, type VideoItem } from "@/apis";

type PlayerKind = "iframe" | "video" | "link";

function resolvePlayer(url: string): { kind: PlayerKind; src: string } {
  const trimmed = url.trim();
  let parsed: URL;
  try {
    parsed = new URL(trimmed);
  } catch {
    return { kind: "link", src: trimmed };
  }

  const host = parsed.hostname.replace(/^www\./, "");
  if (host === "youtu.be") {
    const id = parsed.pathname.split("/").filter(Boolean)[0];
    if (id) return { kind: "iframe", src: `https://www.youtube.com/embed/${id}` };
  }
  if (host === "youtube.com" || host === "m.youtube.com") {
    const watchId = parsed.searchParams.get("v");
    if (watchId) return { kind: "iframe", src: `https://www.youtube.com/embed/${watchId}` };
    const parts = parsed.pathname.split("/").filter(Boolean);
    if ((parts[0] === "embed" || parts[0] === "shorts" || parts[0] === "live") && parts[1]) {
      return { kind: "iframe", src: `https://www.youtube.com/embed/${parts[1]}` };
    }
  }
  if (host === "vimeo.com") {
    const id = parsed.pathname.split("/").filter(Boolean).pop();
    if (id && /^\d+$/.test(id)) {
      return { kind: "iframe", src: `https://player.vimeo.com/video/${id}` };
    }
  }
  if (host === "player.vimeo.com") {
    return { kind: "iframe", src: trimmed };
  }
  if (/\.(mp4|webm|ogg|mov)(\?|$)/i.test(parsed.pathname)) {
    return { kind: "video", src: trimmed };
  }
  return { kind: "link", src: trimmed };
}

export function DashboardVideos() {
  const [videos, setVideos] = useState<VideoItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [active, setActive] = useState<VideoItem | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const data = await videosApi.listForUser();
        if (!cancelled) setVideos(data);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Failed to load videos");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  const player = active
    ? active.source === "upload"
      ? { kind: "video" as const, src: active.videoUrl }
      : resolvePlayer(active.videoUrl)
    : null;

  return (
    <div className="space-y-4">
      {error ? (
        <div className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-700">{error}</div>
      ) : null}

      {loading ? (
        <div className="flex items-center justify-center py-6">
          <div className="h-10 w-10 animate-spin rounded-full bg-gradient-to-r from-primary/60 via-primary to-primary/80 p-[3px]">
            <div className="h-full w-full rounded-full bg-background" />
          </div>
        </div>
      ) : null}

      {!loading && videos.length === 0 ? (
        <div className="py-12 text-center">
          <p className="text-muted-foreground">No videos available.</p>
        </div>
      ) : null}

      {!loading && videos.length > 0 ? (
        <div className="grid grid-cols-2 gap-6 sm:grid-cols-3 lg:grid-cols-6">
          {videos.map((video) => (
            <button
              key={video.id}
              type="button"
              onClick={() => setActive(video)}
              className="group flex items-start gap-3 rounded-xl border border-primary/20 bg-primary/5 p-3 text-left transition-all hover:bg-primary/10 hover:shadow-sm"
            >
              <div className="relative h-10 w-10 shrink-0 overflow-hidden rounded-md bg-black/80">
                {video.thumbnail ? (
                  <img
                    src={video.thumbnail}
                    alt=""
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-primary/30 to-primary/70" />
                )}
                <span className="absolute inset-0 flex items-center justify-center">
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-black/60 text-white">
                    <Play className="h-2.5 w-2.5 fill-current" />
                  </span>
                </span>
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="truncate text-sm font-semibold text-black">{video.title}</h3>
                {video.description ? (
                  <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{video.description}</p>
                ) : null}
              </div>
            </button>
          ))}
        </div>
      ) : null}

      {active && player ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 sm:p-6">
          <div className="w-full max-w-7xl overflow-hidden rounded-lg bg-background shadow-xl">
            <div className="flex items-start justify-between gap-4 border-b p-4">
              <div className="min-w-0">
                <h2 className="truncate text-lg font-semibold">{active.title}</h2>
                {active.description ? (
                  <p className="mt-1 text-sm text-muted-foreground">{active.description}</p>
                ) : null}
              </div>
              <button
                type="button"
                onClick={() => setActive(null)}
                className="rounded-md p-1 text-muted-foreground hover:bg-muted"
                aria-label="Close video"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="bg-black">
              {player.kind === "iframe" ? (
                <iframe
                  title={active.title}
                  src={player.src}
                  className="aspect-video w-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              ) : null}
              {player.kind === "video" ? (
                <video src={player.src} controls className="aspect-video w-full" />
              ) : null}
              {player.kind === "link" ? (
                <div className="flex aspect-video flex-col items-center justify-center gap-3 px-6 text-center text-white">
                  <p className="text-sm text-white/80">This video opens in a new tab.</p>
                  <a
                    href={player.src}
                    target="_blank"
                    rel="noreferrer"
                    className="rounded-md bg-white px-4 py-2 text-sm font-medium text-black"
                  >
                    Open video
                  </a>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
